/**
 * The standard backend response envelope and its unwrap helper.
 *
 * Every Flask endpoint built with `infra/http/response_helpers.py` wraps its
 * payload in `{ success, data, error }`. Historically each feature redeclared
 * this shape and reinvented "read the envelope, throw when `success` is false".
 * That logic now lives here exactly once.
 *
 * Use {@link apiGetUnwrapped} for the COMMON case — a response shaped
 * `{ success, data, ... }` where the UI only wants `data` and a thrown error
 * on failure. Endpoints that put their payload at the TOP level (e.g.
 * `{ success, dates: [...] }`) are genuinely irregular: keep a bespoke fetcher
 * with `apiGet` + a hand-written check for those.
 */

import { apiGet, type ApiRequestOptions } from './client';

/**
 * Standard `{ success, data, error }` envelope returned by `/api/*` routes.
 *
 * The index signature tolerates the extra top-level metadata some endpoints
 * attach (`count`, `source`, `country`, `timeframe`, ...) so callers that read
 * the whole envelope keep compiling without per-feature redeclarations.
 */
export interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  error?: string;
  count?: number;
  source?: string;
  [key: string]: unknown;
}

/**
 * Unwrap a standard envelope: return `envelope.data` on success, throw with
 * `envelope.error` (or a path-derived fallback) when `success` is false.
 */
export function unwrapEnvelope<T>(envelope: ApiEnvelope<T>, path: string): T {
  if (!envelope.success) {
    throw new Error(envelope.error ?? `Request to ${path} failed`);
  }
  return envelope.data;
}

/**
 * `apiGet` + {@link unwrapEnvelope}: fetch a `{ success, data }` endpoint and
 * resolve directly to `data`, throwing on `success: false`. Drop-in for the
 * private `unwrap<T>()` helpers feature `api.ts` files used to carry.
 */
export async function apiGetUnwrapped<T>(
  path: string,
  options?: ApiRequestOptions,
): Promise<T> {
  const envelope = await apiGet<ApiEnvelope<T>>(path, options);
  return unwrapEnvelope(envelope, path);
}
